import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { EroiSection } from "@/components/EroiSection";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Target, Trophy, GraduationCap, Users2, TrendingUp, Rocket,
  ArrowRight, CheckCircle2, Zap, Gauge, HeartHandshake, Sparkles,
  Network, Briefcase, UserCog, Brain, ShieldCheck, Compass,
} from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1689718107045-513b35f1a356?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwb3JhbmdlJTIwZ2VvbWV0cmljJTIwdGVjaG5vbG9neSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgzMzM2MTk1fDA&ixlib=rb-4.1.0&q=85";
const OFFICE =
  "https://images.pexels.com/photos/7642116/pexels-photo-7642116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  { icon: Target, title: "Objetivos & Pontos", desc: "Define metas individuais e de equipa. Cada comportamento de excelência é recompensado com pontos." },
  { icon: Trophy, title: "Rankings Saudáveis", desc: "Cooperação na competição: rankings que reconhecem esforço e evolução, não apenas o topo." },
  { icon: GraduationCap, title: "Academia de Auto-Educação", desc: "Módulos, artigos e quizzes — alguns gerados por IA — para consultores atingirem a excelência." },
  { icon: Users2, title: "CRM com Automatismos", desc: "Gestão de agências clientes com pipeline, atividades e automatismos por cada fase do funil." },
  { icon: Gauge, title: "Dashboard de Performance", desc: "Visão em tempo real de progresso, níveis, badges e evolução semanal de cada colaborador." },
  { icon: Rocket, title: "Onboarding Guiado", desc: "Integração de novos consultores passo a passo, com pontos de boas-vindas e primeiros hábitos." },
];

const STEPS = [
  { n: "01", title: "Diagnóstico", desc: "Analisamos a agência e definimos os comportamentos-chave a reforçar." },
  { n: "02", title: "Configuração", desc: "Ativamos objetivos, pontos, badges e conteúdos de auto-educação à medida." },
  { n: "03", title: "Envolvimento", desc: "Onboarding de toda a equipa — do gestor ao consultor — na A4G League." },
  { n: "04", title: "Excelência", desc: "Acompanhamento contínuo, rankings e evolução de hábitos eficazes." },
];

const PROFILES = [
  { icon: Network, t: "Master Franchising", d: "Visão consolidada de toda a rede." },
  { icon: Briefcase, t: "Broker / Team Leader", d: "Gestão da agência e das equipas." },
  { icon: HeartHandshake, t: "Coach", d: "Acompanhamento e evolução dos consultores." },
  { icon: Users2, t: "Consultor", d: "Objetivos, pontos e hábitos de excelência." },
  { icon: UserCog, t: "Staff", d: "Apoio e envolvimento de toda a estrutura." },
];

const ARCHETYPES = [
  { icon: Brain, t: "Analista", c: "#3B6EFF" },
  { icon: HeartHandshake, t: "Diplomata", c: "#F7861F" },
  { icon: ShieldCheck, t: "Sentinela", c: "#22C55E" },
  { icon: Compass, t: "Explorador", c: "#A855F7" },
];

export default function Landing() {
  const scheduleMeeting = () => {
    window.location.href =
      "mailto:geral@alliance4growth.pt?subject=" +
      encodeURIComponent("Reunião — conhecer a A4G League") +
      "&body=" +
      encodeURIComponent(
        "Olá equipa Alliance 4 Growth,\n\nGostaríamos de agendar uma reunião para conhecer melhor a A4G League para a nossa rede/agência.\n\nRede/Agência:\nNº de consultores:\nContacto:\nDisponibilidade:\n\nObrigado."
      );
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 h-16 border-b border-brand-border bg-[#050505]/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto h-full px-5 flex items-center justify-between">
          <Link to="/" data-testid="league-logo-home"><Logo className="h-8" withText /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-brand-muted">
            <a href="#solucao" className="hover:text-white transition-colors">Solução</a>
            <a href="#organizacao" className="hover:text-white transition-colors">Organização</a>
            <a href="#como" className="hover:text-white transition-colors">Como funciona</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button onClick={scheduleMeeting} data-testid="nav-schedule"
              className="bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold">Agendar reunião</Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-32 pb-24 px-5">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/85 to-[#050505]" />
        </div>
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-brand-orange/10 border border-brand-orange/30 mb-6">
              <Sparkles size={14} className="text-brand-orange" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">Plataforma de crescimento para o imobiliário</span>
            </div>
            <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Transforme a sua agência num{" "}
              <span className="text-brand-blue">campo de excelência</span>{" "}
              com <span className="text-brand-orange">alta performance</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-brand-muted max-w-2xl leading-relaxed">
              A <strong className="text-white">Alliance 4 Growth</strong> combina pontos, objetivos, rankings e auto-educação
              para enraizar hábitos eficazes. Promova o autoconhecimento, a cooperação na competição e o envolvimento de
              todos os consultores — com a <strong className="text-white">A4G League</strong>.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={scheduleMeeting} data-testid="hero-cta"
                className="bg-brand-orange hover:bg-brand-orangehover text-white rounded-sm font-bold text-base px-7 h-12 glow-orange">
                Agendar reunião <ArrowRight size={18} className="ml-1" />
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-brand-muted">
              {["Onboarding em minutos", "Todos os colaboradores envolvidos", "Cooperação na competição"].map((t) => (
                <span key={t} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-success" /> {t}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-brand-border bg-[#0a0b0e]">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-brand-border">
          {[
            { k: "+38%", v: "Angariações por consultor" },
            { k: "3x", v: "Adesão à auto-educação" },
            { k: "100%", v: "Equipa envolvida" },
            { k: "-27%", v: "Rotatividade de talento" },
          ].map((s) => (
            <div key={s.v} className="p-6 text-center">
              <p className="font-heading font-black text-3xl text-brand-orange">{s.k}</p>
              <p className="text-xs text-brand-muted mt-1">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="solucao" className="py-24 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-orange">A solução</span>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3">
              Uma plataforma que reforça comportamentos e enraíza hábitos
            </h2>
            <p className="text-brand-muted mt-4">
              Tudo o que precisa para levar cada consultor a patamares de excelência — num só sítio.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group p-6 rounded-sm bg-brand-surface border border-brand-border hover:border-brand-blue/50 hover:-translate-y-1 transition-all duration-200">
                <div className="h-11 w-11 rounded-sm bg-brand-blue/15 flex items-center justify-center mb-4 group-hover:bg-brand-blue/25 transition-colors">
                  <f.icon size={20} className="text-brand-blue" />
                </div>
                <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
                <p className="text-sm text-brand-muted mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <EroiSection variant="league" />

      {/* SPLIT */}
      <section className="py-16 px-5 bg-[#0a0b0e] border-y border-brand-border">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-orange">Cooperação na competição</span>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mt-3">
              Toda a agência a crescer em conjunto
            </h2>
            <p className="text-brand-muted mt-4 leading-relaxed">
              Do gestor ao consultor mais recente, todos participam. Metas de equipa geram entreajuda,
              enquanto os rankings individuais motivam a superação pessoal.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                { icon: Zap, t: "Reforço positivo de comportamentos-chave" },
                { icon: HeartHandshake, t: "Objetivos coletivos que unem a equipa" },
                { icon: TrendingUp, t: "Autoconhecimento e evolução mensurável" },
              ].map((x) => (
                <li key={x.t} className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-sm bg-brand-orange/15 flex items-center justify-center shrink-0">
                    <x.icon size={16} className="text-brand-orange" />
                  </div>
                  <span className="text-sm text-white/90">{x.t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-sm overflow-hidden border border-brand-border">
            <img src={OFFICE} alt="Consultores imobiliários" className="w-full h-full max-h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b0e] via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* MASTERS FRANCHISING */}
      <section className="py-20 px-5">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1 grid grid-cols-2 gap-4">
            {[
              { icon: Gauge, k: "Adoção da rede", v: "Acompanhe a % de agências e consultores ativos" },
              { icon: Trophy, k: "Ranking agregado", v: "Compare desempenho entre agências da rede" },
              { icon: Rocket, k: "Rollout simples", v: "Ative a A4G League agência a agência" },
              { icon: Users2, k: "Todos envolvidos", v: "Do broker ao consultor, staff e coaches" },
            ].map((c) => (
              <div key={c.k} className="p-5 rounded-sm bg-brand-surface border border-brand-border">
                <div className="h-10 w-10 rounded-sm bg-brand-blue/15 flex items-center justify-center">
                  <c.icon size={18} className="text-brand-blue" />
                </div>
                <p className="font-heading font-semibold mt-3">{c.k}</p>
                <p className="text-xs text-brand-muted mt-1 leading-relaxed">{c.v}</p>
              </div>
            ))}
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-orange">Para Masters Franchising</span>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3">
              Escale a cultura de excelência a toda a rede
            </h2>
            <p className="text-brand-muted mt-4 leading-relaxed">
              Pensado para <strong className="text-white">Masters Franchising</strong>: uma visão consolidada da adoção,
              do desempenho e do envolvimento de cada agência da rede. Facilite a decisão de adoção e ofereça às suas
              agências uma ferramenta que reforça hábitos e resultados.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Dashboard de rede com métricas agregadas",
                "Onboarding e adoção acompanhados por agência",
                "Proposta de valor clara para apresentar às agências",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <CheckCircle2 size={16} className="text-brand-success shrink-0" />
                  <span className="text-sm text-white/90">{t}</span>
                </li>
              ))}
            </ul>
            <Button size="lg" onClick={scheduleMeeting} data-testid="masters-cta"
              className="mt-8 bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold h-12 px-7">
              Agendar reunião para a minha rede <ArrowRight size={18} className="ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="como" className="py-24 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-orange">Como funciona</span>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3">
              Começar a prestar o serviço em 4 passos
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="p-6 rounded-sm bg-brand-surface border border-brand-border">
                <p className="font-heading font-black text-4xl text-brand-blue/40">{s.n}</p>
                <h3 className="font-heading font-semibold text-lg mt-3">{s.title}</h3>
                <p className="text-sm text-brand-muted mt-2">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORGANIZAÇÃO — perfis + personalidade */}
      <section id="organizacao" className="py-24 px-5 bg-[#0a0b0e] border-y border-brand-border">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-orange">Toda a organização</span>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3">
              Envolve todos os perfis — respeita cada personalidade
            </h2>
            <p className="text-brand-muted mt-4 leading-relaxed">
              Da liderança ao consultor, cada perfil tem a sua visão da plataforma. E porque cada pessoa é única,
              reconhecemos a personalidade de cada consultor (MBTI) e adaptamos o reconhecimento a diferentes arquétipos.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {PROFILES.map((p) => (
              <div key={p.t} data-testid={`profile-${p.t}`} className="p-5 rounded-sm bg-brand-surface border border-brand-border">
                <div className="h-10 w-10 rounded-sm bg-brand-blue/15 flex items-center justify-center">
                  <p.icon size={18} className="text-brand-blue" />
                </div>
                <p className="font-heading font-semibold mt-3 text-sm">{p.t}</p>
                <p className="text-xs text-brand-muted mt-1 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-orange mb-4">Reconhecimento por personalidade (MBTI)</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {ARCHETYPES.map((a) => (
                <div key={a.t} className="p-5 rounded-sm bg-brand-surface border border-brand-border flex items-center gap-3" style={{ borderTopColor: a.c, borderTopWidth: 2 }}>
                  <div className="h-10 w-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${a.c}22` }}>
                    <a.icon size={18} style={{ color: a.c }} />
                  </div>
                  <span className="font-heading font-semibold text-sm">{a.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA — reunião */}
      <section id="reuniao" className="py-24 px-5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">
            Vamos conhecer melhor a sua rede?
          </h2>
          <p className="text-brand-muted mt-4 max-w-2xl mx-auto">
            Agende uma reunião com a equipa Alliance 4 Growth. Analisamos a realidade da sua rede ou agência e mostramos
            como a A4G League pode elevar a performance e a cultura de toda a organização.
          </p>
          <Button size="lg" onClick={scheduleMeeting} data-testid="footer-cta"
            className="mt-8 bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold h-12 px-8 glow-orange">
            Agendar reunião <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-brand-border py-10 px-5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo className="h-7" withText />
          <p className="text-xs text-brand-muted">© {new Date().getFullYear()} Alliance 4 Growth · A4G League</p>
          <div className="flex gap-4 text-sm text-brand-muted">
            <button onClick={scheduleMeeting} className="hover:text-white" data-testid="footer-schedule">Agendar reunião</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
